import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { NgbActiveModal, NgbDateAdapter, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import * as _ from 'lodash';
import { STUDIO_INQUIRY_PROJECT_INFO } from '../../constants/common';
import { INDEX } from '../../enum/shared.enum';
import { CustomAdapter } from '../../services/date-picker/new-date-picker-service.service';
import { CustomDateParserFormatter } from '../../services/date-picker/new-date-picker.service';
import { FormValidationService } from '../../services/form-validations/form-validation.service';

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    selector: 'app-studio-inquiry-project-information',
    templateUrl: './studio-inquiry-project-information.component.html',
    styleUrls: ['./studio-inquiry-project-information.component.scss'],
    standalone: false,
    providers: [
        { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
        { provide: NgbDateAdapter, useClass: CustomAdapter },
    ]
})
export class StudioInquiryProjectInformationComponent implements OnInit {
  /** Services picked on the previous step of the inquiry */
  @Input() selectedServices: { id: string; name: string }[] = [];
  /** Values captured earlier in the flow, so stepping back does not lose them */
  @Input() projectInformation: any;
  @Output() projectInformationSubmitted = new EventEmitter<any>();
  constants = STUDIO_INQUIRY_PROJECT_INFO;
  projectForm!: FormGroup;
  preferredDateFormat: number;
  formatId: INDEX;
  calenderFormat: INDEX;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly formValidationService: FormValidationService,
    private readonly dateAdapter: NgbDateAdapter<string>,
    private readonly dateFormatter: NgbDateParserFormatter,
    public activeModal: NgbActiveModal
  ) { }

  ngOnInit(): void {
    this.changeDateFormat();
    this.projectForm = this.formBuilder.group({
      projectType: ['', Validators.required],
      projectName: ['', Validators.required],
      startDate: ['', [
        Validators.required,
        this.dateRangeValidator,
        this.formValidationService.dateValidatorNew(this.preferredDateFormat)
      ]],
      shootDate: ['', [this.formValidationService.dateValidatorMultiple(this.preferredDateFormat)]],
      endDate: ['', [
        Validators.required,
        this.dateRangeValidator,
        this.formValidationService.dateValidatorMultiple(this.preferredDateFormat)
      ]],
      scheduleNotes: [''],
    });
    if (this.projectInformation) {
      this.projectForm.patchValue(this.projectInformation);
    }
    this.watchDateRange();
    this.getCalendarFormat();
    this.calenderFormat = this.formatId === INDEX.one ? INDEX.seven : INDEX.one;
  }

  /**
   * changeDateFormat() - Reads the user's preferred date format, which drives
   * both the placeholder and how the picker parses what is typed. The inquiry is
   * open to visitors who have no saved preference, so this falls back to
   * m/d/yyyy and shares that with the adapter and formatter, which otherwise
   * discard every date they are handed
   */
  changeDateFormat(): void {
    const userProfile = JSON.parse(localStorage.getItem('user-profile'));
    const updatedData = JSON.parse(localStorage.getItem('updated-profile'));
    const dateFormatId = updatedData
      ? updatedData?.userPreferences?.dateFormatSettings?.dateFormatId
      : userProfile?.userPreferences?.dateFormatSettings?.dateFormatId;
    this.preferredDateFormat = dateFormatId || INDEX.one;
    (this.dateAdapter as CustomAdapter).preferredDateFormat = this.preferredDateFormat;
    (this.dateFormatter as CustomDateParserFormatter).preferredDateFormat = this.preferredDateFormat;
  }

  /**
   * getCalendarFormat() - Reads the user's preferred first day of the week,
   * defaulting to a Sunday start for visitors with no saved preference
   */
  getCalendarFormat(): void {
    const updatedData = JSON.parse(localStorage.getItem('updated-profile'));
    const userProfile = JSON.parse(localStorage.getItem('user-profile'));
    const calenderFormatId = updatedData
      ? updatedData?.userPreferences?.calenderFormatSettings?.calenderFormatId
      : userProfile?.userPreferences?.calenderFormatSettings?.calenderFormatId;
    this.formatId = calenderFormatId || INDEX.one;
  }

  /**
   * getPlaceholder() - Renders the placeholder for the preferred date format
   * @returns the date format hint
   */
  getPlaceholder(): string {
    if (this.preferredDateFormat === INDEX.one) {
      return 'mm/dd/yyyy';
    } else if (this.preferredDateFormat === INDEX.two) {
      return 'dd/mm/yyyy';
    }
    return 'yyyy-mm-dd';
  }

  /**
   * dateRangeValidator() - Flags a start date that falls after the end date
   * @returns an invalidRange error, or null when the dates are in order
   */
  public dateRangeValidator: ValidatorFn = (): { [key: string]: any } | null => {
    const startDate = this.toDate(this.projectForm?.get('startDate')?.value);
    const endDate = this.toDate(this.projectForm?.get('endDate')?.value);
    if (startDate && endDate && startDate > endDate) {
      return { invalidRange: { from: startDate, to: endDate } };
    }
    return null;
  }

  /**
   * onNext() - Emits the captured project information and closes the modal
   */
  onNext(): void {
    const projectInformation = { ...this.projectForm.value, services: this.selectedServices };
    this.projectInformationSubmitted.emit(projectInformation);
    this.activeModal.close(projectInformation);
  }

  /**
   * onBack() - Returns to the service selection step
   */
  onBack(): void {
    this.activeModal.dismiss('back');
  }

  /**
   * closeModal() - Dismisses the inquiry
   */
  closeModal(): void {
    this.activeModal.dismiss();
  }

  /**
   * watchDateRange() - Re-checks the sibling date whenever one end of the range
   * changes, so fixing either field clears the range error from both
   */
  private watchDateRange(): void {
    const pairs: Record<string, string> = { startDate: 'endDate', endDate: 'startDate' };
    Object.keys(pairs).forEach((field) => {
      this.projectForm.get(field)?.valueChanges.subscribe(() => {
        this.projectForm.get(pairs[field])?.updateValueAndValidity({ emitEvent: false });
      });
    });
  }

  /**
   * toDate() - Parses the value the date adapter stores on the control
   * @param value a date in the user's preferred order, delimited by "-"
   * @returns the parsed date, or null when the value is not a full date
   */
  private toDate(value: string): Date | null {
    if (!value) {
      return null;
    }
    const parts = _.map(_.split(value, '-'), Number);
    if (parts.length !== INDEX.three || _.some(parts, isNaN)) {
      return null;
    }
    const [first, second, year] = parts;
    const month = this.preferredDateFormat === INDEX.two ? second : first;
    const day = this.preferredDateFormat === INDEX.two ? first : second;
    return new Date(year, month - 1, day);
  }
}
